import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Store } from 'lucide-react';
import { useStore } from '../../hooks/useStore';
import Container from './Container';
import Skeleton from '../common/Skeleton';
import EmptyState from '../common/EmptyState';
import StorefrontHeader from './StorefrontHeader';
import StorefrontFooter from './StorefrontFooter';

import SupplierHomePage from '../../pages/supplier/SupplierHomePage';
import SupplierCatalogPage from '../../pages/supplier/SupplierCatalogPage';
import ProductDetailPage from '../../pages/product/ProductDetailPage';
import CartPage from '../../pages/cart/CartPage';
import CheckoutPage from '../../pages/checkout/CheckoutPage';
import StoreUnavailablePage from '../../pages/storefront/StoreUnavailablePage';

// Roteador específico da whitelabel via path legado /fornecedor/:slug/*.
// Decide QUAIS rotas registrar baseado em store.businessType. Em catalog
// mode, /carrinho e /checkout nem existem.
//
// Layout: aplica StorefrontHeader/Footer aqui mesmo — diferente do
// PublicLayoutSwitch que serve as rotas raiz, o StorefrontRouter foi montado
// fora dele em App.js.
const StorefrontShell = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-app-bg">
    <StorefrontHeader />
    <main className="flex-1">{children}</main>
    <StorefrontFooter />
  </div>
);

const StorefrontRouter = () => {
  const { store, loading } = useStore();

  if (loading) {
    return (
      <Container>
        <Skeleton variant="block" height={120} className="my-8" />
      </Container>
    );
  }

  if (!store) {
    return (
      <Container>
        <EmptyState
          icon={Store}
          title="Loja não encontrada"
          description="O link pode ter sido alterado ou a loja foi removida."
        />
      </Container>
    );
  }

  // Loja bloqueada pela plataforma (Seção 2.3.6): substitui todas as rotas
  // do whitelabel pela tela "serviço indisponível". MANAGER segue acessando
  // o backoffice (que tem rotas separadas, fora deste roteador).
  if (store.isBlocked) {
    return (
      <Routes>
        <Route path="*" element={<StoreUnavailablePage />} />
      </Routes>
    );
  }

  // Rotas comuns a todos os businessTypes — sempre registradas.
  const common = (
    <>
      <Route index element={<SupplierHomePage />} />
      <Route path="catalogo" element={<SupplierCatalogPage />} />
      <Route path="produto/:id" element={<ProductDetailPage />} />
    </>
  );

  if (store.businessType === 'catalog') {
    return (
      <StorefrontShell>
        <Routes>
          {common}
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </StorefrontShell>
    );
  }

  // Seller e reseller têm fluxo completo de compra.
  return (
    <StorefrontShell>
      <Routes>
        {common}
        <Route path="carrinho" element={<CartPage />} />
        <Route path="checkout" element={<CheckoutPage />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </StorefrontShell>
  );
};

export default StorefrontRouter;
